/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../apiHelpers/SlotAPIHelper',
	'../../../lib/@waca/dashboard-common/dist/utils/ContentUtil',
	'../../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'underscore'
],
(SlotAPIHelper, ContentUtil, BrowserUtils, _) => {

	return class DataQueryUtils {

		/**
		 * @public
		 * Convert exclude empty values filters to include all filters for non OLAP data sources since there might be joins defined in model,
		 * and remove such filters for OLAP sources since OLAP doesn't have the concept of joins so these filters won't affect the query at all.
		 * @param {filterSpec[]} filterSpecList
		 * @example
		 * Convert exclude all filter from
		 * {
		 * 'id': 'dataSetDemo_csv.Product_line',
		 * 'columnId': 'dataSetDemo_csv.Product_line',
		 * 'values': [],
		 * 'excludedValues': [],
		 * 'operator': 'notin',
		 * 'type': null
		 * }
		 *  to
		 * {
		 * 	"operator": "in",
		 * 	"itemId": "dataSetDemo_csv.Product_line",
		 * 	"values": ["dataSetDemo_csv.Product_line"]
		 * }
		 */
		static convertExcludeEmptyValuesFilters(filterSpecList, dataSource) {
			let remainingFilters = [];
			const excludeEmptyValuesFilters = _.filter(filterSpecList, filterSpec => {
				let isExcludeEmptyValues = filterSpec.operator === 'notin' && (!filterSpec.values || !filterSpec.values.length) && (!filterSpec.conditions);
				if (!isExcludeEmptyValues) {
					remainingFilters.push(filterSpec);
				}
				return isExcludeEmptyValues;
			});

			if (excludeEmptyValuesFilters) {
				excludeEmptyValuesFilters.forEach(excludeEmptyValuesFilter => {
					const column = dataSource.getMetadataColumn(excludeEmptyValuesFilter.columnId);
					if (!column.isOlapColumn()) {
						let valueDataItemSpec = {
							id: excludeEmptyValuesFilter.id || excludeEmptyValuesFilter.columnId,
							itemId: excludeEmptyValuesFilter.columnId
						};
						let filterSpec = {
							operator: 'in',
							columnId: valueDataItemSpec.itemId
						};

						filterSpec.values = [{ u: valueDataItemSpec.itemId, d: valueDataItemSpec.itemId }];
						remainingFilters.push(filterSpec);
					}
				});
			}
			return remainingFilters;
		}

		static findFirstKeyOfMap(mapObj) {
			/** the code here is to support IE. */
			const allKeys = [];
			mapObj.forEach((val, key)=>{
				allKeys.push(key);
			});
			return allKeys[0];
		}

		/**
		 * @public
		 * NonProjected range filters cannot be applied to any layer whose categories are not the same as the first mapped layer
		 * (because the aggregate of different categories is different.)
		 */
		static shouldExcludeNonProjectedRangeFilters(layerToQuerySlotsMap, layerId) {
			let excludeNonProjectedRangeFilters = layerToQuerySlotsMap.size; // Should have at least one mapped layer.
			if (!excludeNonProjectedRangeFilters) {
				return false;
			}
			const firstKey = DataQueryUtils.findFirstKeyOfMap(layerToQuerySlotsMap);
			let categoryItemsForFirstMappedLayer = DataQueryUtils._getCategoryItemsForLayer(layerToQuerySlotsMap.get(firstKey)) || [];
			const categoryItemsForThisLayer = DataQueryUtils._getCategoryItemsForLayer(layerToQuerySlotsMap.get(layerId)) || [];

			return DataQueryUtils._categoriesAreDifferent(categoryItemsForFirstMappedLayer, categoryItemsForThisLayer);
		}

		//@returns true if categorySet1 and categorySet2 are different (in both directions)
		static _categoriesAreDifferent(categorySet1, categorySet2) {
			return (_.difference(categorySet1 || [], categorySet2 || []).length > 0 ||
					_.difference(categorySet2 || [], categorySet1 || []).length > 0);
		}

		//@returns the categorical items for the specified layer
		static _getCategoryItemsForLayer(slots) {
			let columnIdList = [];
			slots && slots.forEach(slot => {
				columnIdList.push(..._.map(slot.getDataItemList(), dataItem => dataItem.getColumnId()));
			});
			return columnIdList;
		}

		static removeForecastFilters(spec) {
			//@returns querySpecList with values of form '"__ibm_ba_forecast__[0-9]*"' replaced by '""'.
			// Defect 286105: This is a temporary fix for sending filters on forecasted points to query service without them complaining
			// Resulting response would be empty if only a forecasted point filter is sent, or filtering on other selections
			// if filter includes other values. Filtering on forecasted points happens only when viz are brushed/filtered.
			let regex = new RegExp('"__ibm_ba_forecast__[0-9]*"','g');
			return JSON.parse(JSON.stringify(spec).replace(regex, '""'));
		}

		/**
		 * Get the list of global filters that applies to the given visualization
		 * @param {VisualizationAPI} visualization
		 * @returns {Object[]} list of global filter specifications
		 */
		static getFilterSpecListByVisualization(content, dashboard) {
			const eventGroups = dashboard.getFeature('EventGroups');
			const globalFilters = dashboard.getFeature('GlobalFilters');
			const visualization = content.getFeature('Visualization');
			const filterQuerySpecList = [];
			const dataSource = visualization.getDataSource();

			if (dataSource) {
				const globalFilterList = globalFilters.getFilterList({
					origin: 'filter',
					sourceId: dataSource.getId(),
					scope: DataQueryUtils.getPageId(dashboard, content)
				}, content.getId());

				const hasJoinError = dataSource.hasJoinError(DataQueryUtils.getReferencedTableList({ visualization, content, dashboard, globalFilterList }));
				const applicableGlobalFilters = (!hasJoinError) ? globalFilterList : DataQueryUtils.getSafeGlobalFilterList(
					{ visualization, dataSource, globalFilterList }
				);
				filterQuerySpecList.push(...applicableGlobalFilters);
				const brushingBehavior = content.getPropertyValue('brushing');
				if (visualization.getDefinition().getProperty('reactToExternalBrushing') !== false && brushingBehavior !== 'ignoreFilter') {
					const eventGroupId = eventGroups.getGroupId(content.getId());
					filterQuerySpecList.push(...globalFilters.getFilterList({
						origin: 'visualization',
						sourceId: dataSource.getId(),
						scope: DataQueryUtils.getPageId(dashboard, content),
						eventGroupId: eventGroupId
					}, brushingBehavior === 'alwaysFilter' ? null : content.getId()));
				}
			}
			return filterQuerySpecList;
		}

		static getPageId(dashboard, content) {
			const type = dashboard.getAppConfig('pageContainerType');
			const page = ContentUtil.getPageContent(content, type);
			return page && page.getId();
		}

		static getReferencedTableList({ visualization, content, dashboard, globalFilterList=null }) {
			//Return a list of all tables referenced by a visualization or global filters for its datasource.
			const dataSource = visualization.getDataSource();
			const globalFilters = dashboard.getFeature('GlobalFilters');
			if (dataSource && globalFilters) {
				//Get the list of tables used in the visualization
				const usedTableIds = DataQueryUtils.getReferencedTablesForVisualizationAsSet(visualization);

				//Get the list of tables referenced in global filters (that have values) for this dataSource.
				globalFilterList = globalFilterList || globalFilters.getFilterList({
					origin: 'filter',
					sourceId: dataSource.getId(),
					scope: DataQueryUtils.getPageId(dashboard, content)
				}, content.getId());
				globalFilterList.forEach((gf) => {
					const gfMetadataColumn = dataSource.getMetadataColumn(gf.columnId);
					const gfTableId = gfMetadataColumn && gfMetadataColumn.getTableName();
					if (gfTableId) {
						usedTableIds[gfTableId]=true;
					}
				});
				return Object.keys(usedTableIds);
			}
			return [];
		}

		static getReferencedTablesForVisualizationAsSet(visualization) {
			//Populate a set with the table id's used by a visualization. 
			const usedMdColumns = visualization.getSlots().getMappingInfoList().map((mi) => mi.dataItem.getMetadataColumn());
			const usedTableIds = {};
			usedMdColumns.forEach((mdColumn) => {
				usedTableIds[mdColumn.getTableName()]=true;
			});
			return usedTableIds;
		}

		static getSafeGlobalFilterList({ visualization, dataSource, globalFilterList }) {
			//The "SAFE" global filter list is a list of global filters that access tables that are already accessed
			//by the visualization (ie: they should not cause join errors).

			//Get the list of tables used in the visualization
			const usedTableIds = DataQueryUtils.getReferencedTablesForVisualizationAsSet(visualization);

			//Only return global filters that reference a table referenced in the vis.
			const finalGlobalFilterList = globalFilterList.filter((gf) => {
				const gfMetadataColumn = dataSource.getMetadataColumn(gf.columnId);
				const gfTableId = gfMetadataColumn && gfMetadataColumn.getTableName();
				return usedTableIds[gfTableId];
			});
			return finalGlobalFilterList;
		}

		/**
         * @function DataQueryUtils#getRelatedModelItemsForQueryHint
         * @description Get related model items used for query hint preferredModelItems
         * @public
         * @param {Object} Content API 
		 * @param {Object} Dashboard API
         * @return {Array} An arry of related model items
         */
		static getRelatedModelItemsForQueryHint(content, dashboard) {
			const eventGroups = dashboard.getFeature('EventGroups');
			const visualization = content.getFeature('Visualization');
			const dataSource = visualization.getDataSource();
			const sourceId = dataSource.getId();
			const contentId = content.getId();

			const groupId = eventGroups.getGroupId(contentId);
			const contentIdList = eventGroups.getContentIdList(groupId);
			if (!contentIdList) {
				return [];
			}
			const canvas = dashboard.getFeature('Canvas');
			let otherContentsInSameGroup = contentIdList.filter(id => id !== contentId)
				.map(id => canvas.getContent(id));
			// TODO: back to it in R5, when deleting a widget, it has to get deleted from content.
			// Also, the content is null for the case we open a saved dashboard that it's vipr bundle is deleted, so it is in error state.
			// when deleting it, the content has null value
			otherContentsInSameGroup =  otherContentsInSameGroup.filter(content => {
				let isValid = !!content;
				if (isValid) {
					const visualization = content.getFeature('Visualization');
					const visDefinition = visualization && visualization.getDefinition();
					isValid = !(visDefinition && visDefinition.getState().getError());
				}
				return isValid;
			});

			const otherContentsInSameGroupWithSameSource = otherContentsInSameGroup.filter(content=>{
				const otherVisualization = content.getFeature('Visualization');
				const dataSource = otherVisualization && otherVisualization.getDataSource();
				return dataSource && dataSource.getId() === sourceId;
			});

			const columnIdSet = new Set();

			otherContentsInSameGroupWithSameSource.forEach(content=>{
				const slots = content.getFeature('Visualization').getSlots();
				slots.getDataItemList().forEach(dataItem=>{
					// skip the fake multimeasure series dataitem
					if (!SlotAPIHelper.isMultiMeasuresSeriesOrValueDataItem(dataItem)){
						const metadataColumn = dataItem.getMetadataColumn();
						if (metadataColumn && !metadataColumn.isOlapColumn()) {
							// Add related column id only when it exists and is not an olap column.
							columnIdSet.add(metadataColumn.getId());
						}
					}
				});
			});

			return Array.from(columnIdSet);
		}

		static getRowLimit(visDefinition) {
			// Some charts have difficulty rendering a large quantity of data, 
			// in this case, a smaller dataLimit is set in the definition.

			if (BrowserUtils.isIE() && visDefinition.getProperty('dataLimitIE')) {
				// Reduce data limit on IE
				return visDefinition.getProperty('dataLimitIE');
			} else if (visDefinition.getProperty('dataRowLimit')) {
				return visDefinition.getProperty('dataRowLimit');
			}
		}
	};

});
